import { Component, NgZone, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CatalogueApiService, SearchCriteria } from '@angular-workspace/services';

export interface StatItem {
  icon: string;
  label: string;
  apiType?: string;
  displayValue: number;
}

@Component({
  selector: 'app-statistics',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './statistics.component.html',
  styleUrl: './statistics.component.scss',
})
export class StatisticsComponent implements OnInit {
  stats: StatItem[] = [
    {
      icon: '/asset-dataBank.svg',
      label: 'Datasets',
      apiType: 'adex:DataBank',
      displayValue: 0
    },
    {
      icon: '/asset-ai-model.svg',
      label: 'Models',
      apiType: 'adex:AiModel',
      displayValue: 0
    },
    {
      icon: '/asset-use-case.svg',
      label: 'Use Cases',
      displayValue: 11
    }
  ];

  constructor(
    private catalogueApiService: CatalogueApiService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.loadStatistics();
  }

  async loadStatistics(): Promise<void> {
    try {
      const searchCriteria: SearchCriteria[] = [
        {
          searchType: 'term',
          field: 'type',
          values: ['adex:DataBank', 'adex:AiModel']
        }
      ];

      const response = await this.catalogueApiService.getCount(searchCriteria);
      const countData = this.extractCountRow(response);
      if (!countData) {
        return;
      }

      this.stats.forEach((stat) => {
        if (!stat.apiType) {
          return;
        }
        const targetValue = this.readCountForApiType(countData, stat.apiType);
        if (targetValue === undefined) {
          return;
        }
        this.animateStat(stat, targetValue);
      });
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  }

  private extractCountRow(response: unknown): Record<string, unknown> | null {
    if (response == null || typeof response !== 'object') {
      return null;
    }
    const res = response as Record<string, unknown>;
    const raw = res['result'] ?? res['data'] ?? res['results'];
    if (raw == null) {
      return null;
    }
    if (Array.isArray(raw)) {
      const first = raw[0];
      if (first != null && typeof first === 'object' && !Array.isArray(first)) {
        return first as Record<string, unknown>;
      }
      return null;
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return raw as Record<string, unknown>;
    }
    return null;
  }

  private readCountForApiType(row: Record<string, unknown>, canonicalKey: string): number | undefined {
    const tryKey = (k: string): number | undefined => {
      const v = row[k];
      if (v === undefined || v === null) {
        return undefined;
      }
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };

    const direct = tryKey(canonicalKey);
    if (direct !== undefined) {
      return direct;
    }
    const lowerCanon = canonicalKey.toLowerCase();
    for (const k of Object.keys(row)) {
      if (k.toLowerCase() === lowerCanon) {
        return tryKey(k);
      }
    }
    return undefined;
  }

  private animateStat(stat: StatItem, target: number): void {
    const duration = 1500;
    const startValue = 0;
    const valueDifference = target - startValue;
    let startTime: number | null = null;

    const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

    const step = (timestamp: number) => {
      if (startTime === null) {
        startTime = timestamp;
      }
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      const currentValue = startValue + valueDifference * easedProgress;
      const next = progress < 1 ? Math.floor(currentValue) : target;

      this.ngZone.run(() => {
        stat.displayValue = next;
      });

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }
}

